import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { FiSearch, FiX, FiTruck, FiExternalLink } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import {
  WINERS_CAR_LIST,
  ACTIVE_BUYING_LIST,
} from "../../constant";
import { GET, POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { getAuthUserId } from "../../services/localStorage";
import { sanitizeImageUrl } from "../../utils/sanitizeImageUrl";
import DetailDrawer from "../DetailDrawer";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const TABS = [
  {
    key: "won",
    name: "Won auctions",
    emptyTitle: "No won lots awaiting shipping",
    emptyCopy:
      "When you win an auction, settlement and delivery options will show here.",
  },
  {
    key: "purchases",
    name: "Purchases",
    emptyTitle: "No active purchases",
    emptyCopy:
      "Buy-now settlements and delivery progress will appear in this list.",
  },
];

function toNumber(value) {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `€${num.toLocaleString()}`;
}

function pick(item, keys, fallback = "—") {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== "") return value;
  }
  return fallback;
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.product_name,
    item?.product_description,
    item?.registration,
    item?.registration_number,
    item?.status,
    item?.product_id,
    item?.your_bid,
    item?.product_price,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function Shiping({
  user_id,
  winerList,
  winerListloading,
  activeBuying,
  activeBuyingLoading,
  getWinners,
  getActiveBuying,
}) {
  const history = useHistory();
  const [tabIdx, setTabIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewingItem, setViewingItem] = useState(null);

  const tab = TABS[tabIdx];
  const id = user_id || getAuthUserId();
  const isWonTab = tab.key === "won";
  const sourceList = isWonTab
    ? Array.isArray(winerList)
      ? winerList
      : []
    : Array.isArray(activeBuying)
    ? activeBuying
    : [];
  const loading = isWonTab ? winerListloading : activeBuyingLoading;
  const isLoading = Boolean(loading) && sourceList.length === 0;

  useEffect(() => {
    if (!id) return;
    if (isWonTab) getWinners(id);
    else getActiveBuying(id);
  }, [id, tabIdx]);

  useEffect(() => {
    setPage(1);
    setSearch("");
    setSortBy("name_asc");
    setViewingItem(null);
  }, [tabIdx]);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, pageSize]);

  const filteredList = useMemo(() => {
    let rows = [...sourceList].filter((item) => matchesSearch(item, search));

    rows.sort((a, b) => {
      const priceA = toNumber(
        pick(a, ["your_bid", "product_price", "price", "winningBid"], 0)
      );
      const priceB = toNumber(
        pick(b, ["your_bid", "product_price", "price", "winningBid"], 0)
      );
      if (sortBy === "price_desc") return priceB - priceA;
      if (sortBy === "price_asc") return priceA - priceB;
      if (sortBy === "name_desc") {
        return String(b?.product_name || "").localeCompare(
          String(a?.product_name || "")
        );
      }
      return String(a?.product_name || "").localeCompare(
        String(b?.product_name || "")
      );
    });

    return rows;
  }, [sourceList, search, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pageRows = filteredList.slice(pageStart, pageEnd);
  const hasActiveFilters = Boolean(search) || sortBy !== "name_asc";

  const clearFilters = () => {
    setSearch("");
    setSortBy("name_asc");
    setPage(1);
  };

  const bookShipping = (item) => {
    history.push({
      pathname: "/dealers/shipingform",
      state: { item },
    });
  };

  return (
    <div className="dd-section">
      <nav className="dd-tabs" aria-label="Payments and shipping sections">
        {TABS.map((item, index) => {
          const count =
            index === 0
              ? Array.isArray(winerList)
                ? winerList.length
                : 0
              : Array.isArray(activeBuying)
              ? activeBuying.length
              : 0;
          return (
            <button
              key={item.key}
              type="button"
              className={`dd-tab${index === tabIdx ? " is-active" : ""}`}
              onClick={() => setTabIdx(index)}
            >
              {item.name}
              <span className="dd-tab__count">{count}</span>
            </button>
          );
        })}
      </nav>

      {isLoading ? (
        <div className="dd-empty" aria-busy="true">
          <strong>Loading settlement…</strong>
          <p>Fetching payment and shipping items for your dealer desk.</p>
        </div>
      ) : (
        <>
          <div className="dd-toolbar">
            <label className="dd-toolbar__search">
              <FiSearch size={16} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vehicle, status, price…"
                aria-label="Search payments and shipping"
              />
              {search ? (
                <button
                  type="button"
                  className="dd-toolbar__clear-input"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              ) : null}
            </label>

            <label className="dd-toolbar__field">
              <span>Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name_asc">Name · A–Z</option>
                <option value="name_desc">Name · Z–A</option>
                <option value="price_desc">Amount · high to low</option>
                <option value="price_asc">Amount · low to high</option>
              </select>
            </label>

            <label className="dd-toolbar__field">
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            {hasActiveFilters ? (
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={clearFilters}
              >
                Reset
              </button>
            ) : null}
          </div>

          <div className="dd-toolbar__meta">
            {filteredList.length ? (
              <span>
                Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
                {filteredList.length !== sourceList.length
                  ? ` (filtered from ${sourceList.length})`
                  : ""}
              </span>
            ) : (
              <span>No matching items</span>
            )}
          </div>

          {!sourceList.length ? (
            <div className="dd-empty">
              <strong>{tab.emptyTitle}</strong>
              <p>{tab.emptyCopy}</p>
            </div>
          ) : !filteredList.length ? (
            <div className="dd-empty">
              <strong>No matches</strong>
              <p>Try a different search or reset filters.</p>
            </div>
          ) : (
            <>
              <div className="dd-table-wrap dd-ship-table">
                <table className="dd-table">
                  <thead>
                    <tr>
                      <th scope="col">Vehicle</th>
                      <th scope="col">{isWonTab ? "Your bid" : "Amount"}</th>
                      <th scope="col">Status</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((item, index) => {
                      const key =
                        pick(item, ["product_id", "id", "p_id"], null) ||
                        `${tab.key}-${pageStart + index}`;
                      const title = pick(
                        item,
                        ["product_name", "vehicle", "name"],
                        "Untitled lot"
                      );
                      const image = sanitizeImageUrl(
                        pick(item, ["main_image", "product_image"], null)
                      );
                      const amount = formatMoney(
                        pick(
                          item,
                          ["your_bid", "product_price", "price", "winningBid"],
                          null
                        )
                      );
                      const status = pick(
                        item,
                        ["status", "auction_status", "shipping_status"],
                        "Pending"
                      );
                      const description = pick(
                        item,
                        ["product_description", "description"],
                        ""
                      );

                      return (
                        <tr key={key}>
                          <td>
                            <div className="dd-fav-vehicle">
                              <span
                                className="dd-fav-vehicle__thumb"
                                aria-hidden
                              >
                                {image ? (
                                  <img src={image} alt="" />
                                ) : (
                                  <span className="dd-fav-vehicle__placeholder">
                                    —
                                  </span>
                                )}
                              </span>
                              <div className="dd-fav-vehicle__copy">
                                <strong>{title}</strong>
                                {description ? <span>{description}</span> : null}
                              </div>
                            </div>
                          </td>
                          <td>{amount}</td>
                          <td>
                            <span
                              className={`dd-chip ${
                                isWonTab
                                  ? "dd-chip--auction"
                                  : "dd-chip--buynow"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                          <td>
                            <div className="dd-actions">
                              <button
                                type="button"
                                className="dd-action"
                                title="View details"
                                onClick={() => setViewingItem(item)}
                              >
                                <FiExternalLink size={14} aria-hidden />
                                <span>View</span>
                              </button>
                              <button
                                type="button"
                                className="dd-action"
                                title="Book shipping"
                                onClick={() => bookShipping(item)}
                              >
                                <FiTruck size={14} aria-hidden />
                                <span>Book shipping</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredList.length > pageSize ? (
                <div className="dd-pagination">
                  <PaginationControl
                    page={safePage}
                    between={3}
                    total={filteredList.length}
                    limit={pageSize}
                    changePage={(next) => setPage(next)}
                    ellipsis={1}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      <DetailDrawer
        item={viewingItem}
        eyebrow={`Payments & shipping · ${tab.name}`}
        onClose={() => setViewingItem(null)}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_id: state.auth?.id,
  winerList: state.shiping.winerList,
  winerListloading: state.shiping.winerListloading,
  activeBuying: state.product.active_buying,
  activeBuyingLoading: state.product.loadingActive_buyingList,
});

const mapDispatchToProps = (dispatch) => ({
  getWinners: (userId) =>
    dispatch({
      type: WINERS_CAR_LIST,
      payload: {
        type: WINERS_CAR_LIST,
        label: `${Labels.car_winer_list}${userId}/`,
        method: GET,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
  getActiveBuying: (userId) =>
    dispatch({
      type: ACTIVE_BUYING_LIST,
      payload: {
        type: ACTIVE_BUYING_LIST,
        method: POST,
        data: { id_token: userId },
        label: Labels.active_buying_list,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Shiping);
